import React, { useEffect, useRef, useState } from 'react';
import { licencesApi, errorOf } from '../../lib/licences';
import { useConfirm } from '../ConfirmDialog';
import './Licences.css';

/**
 * Give a seat of a licence - to a person, or to a device found by a search -
 * as its type allows: per person to people, per device to devices, a site or
 * perpetual licence to either. One past the seats bought asks first (the
 * server's 409 needs_confirm, in its own words) and is given only on "Give
 * it anyway".
 */
export default function SeatGiver({ licence, people = [], heldBy = { users: [], assets: [] }, onGiven }) {
  const kinds = licence.licence_type === 'per_user' ? ['user']
    : licence.licence_type === 'per_device' ? ['asset'] : ['user', 'asset'];
  const [kind, setKind] = useState(kinds[0]);
  const [userId, setUserId] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [device, setDevice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [confirm, confirmDialog] = useConfirm();
  const seq = useRef(0);

  useEffect(() => { setKind(kinds[0]); }, [licence.licence_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // The device search, as it is typed; the last answer wins.
  useEffect(() => {
    if (kind !== 'asset') return undefined;
    const n = ++seq.current;
    const t = setTimeout(() => {
      licencesApi.devices(q.trim()).then((list) => { if (n === seq.current) setFound(list); }).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q, kind]);

  if (licence.status !== 'active') {
    return <p className="as-quiet" data-testid="seat-giver">{`This licence is ${licence.status}, so it has no seats to give.`}</p>;
  }

  const free = people.filter((p) => !heldBy.users.includes(p.id));
  const give = async (allowOver = false) => {
    const body = kind === 'user' ? { user_id: userId } : { asset_id: device?.id };
    if (!body.user_id && !body.asset_id) return;
    setBusy(true);
    setProblem('');
    try {
      const r = await licencesApi.assign(licence.id, { ...body, allow_over: allowOver });
      setUserId(''); setDevice(null); setQ('');
      onGiven(r);
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.needs_confirm && !allowOver) {
        setBusy(false);
        const yes = await confirm({ title: 'All the seats are in use', message: d.error, confirmLabel: 'Give it anyway' });
        if (yes) await give(true);
        return;
      }
      setProblem(errorOf(e, 'The seat could not be given.'));
    }
    setBusy(false);
  };

  return (
    <div data-testid="seat-giver">
      <div className="lic-give">
        {kinds.length > 1 && (
          <label>
            Give a seat to
            <select value={kind} onChange={(e) => { setKind(e.target.value); setProblem(''); }} aria-label="Give a seat to">
              <option value="user">A person</option>
              <option value="asset">A device</option>
            </select>
          </label>
        )}
        {kind === 'user' ? (
          <label className="lic-give-field">
            {kinds.length > 1 ? 'Person' : 'Give a seat to'}
            <select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Person to give a seat to">
              <option value="">Choose somebody…</option>
              {free.map((p) => <option key={p.id} value={p.id}>{p.department ? `${p.name} (${p.department})` : p.name}</option>)}
            </select>
          </label>
        ) : (
          <div className="lic-give-field">
            <label>
              {kinds.length > 1 ? 'Device' : 'Give a seat to a device'}
              <input type="search" value={device ? device.asset_tag : q} placeholder="Tag, serial, hostname or who has it"
                aria-label="Find a device"
                onChange={(e) => { setDevice(null); setQ(e.target.value); }} />
            </label>
            {!device && found.length > 0 && (
              <ul className="lic-devices" role="listbox" aria-label="Devices found">
                {found.filter((d) => !heldBy.assets.includes(d.id)).map((d) => (
                  <li key={d.id}>
                    <button type="button" role="option" aria-selected={false} onClick={() => setDevice(d)}>
                      <span className="as-tag">{d.asset_tag}</span>
                      {` ${[d.manufacturer, d.model].filter(Boolean).join(' ')}`}
                      <span className="lic-seat-sub">{[d.category_name, d.holder_name ? `with ${d.holder_name}` : null, d.hostname].filter(Boolean).join(' · ')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button type="button" className="btn btn-primary" disabled={busy || (kind === 'user' ? !userId : !device)}
          onClick={() => give(false)}>
          {busy ? 'Giving…' : 'Give a seat'}
        </button>
      </div>
      {problem && <p className="as-error" role="alert">{problem}</p>}
      {confirmDialog}
    </div>
  );
}
